import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, MessageSquare, Send, Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["Nouveau", "En cours", "Devis envoyé", "Signé", "Perdu"];
const QUALITIES = ["Non qualifié", "Froid", "Tiède", "Chaud"];
const statusColor = {
  "Nouveau": "bg-blue-100 text-blue-700", "En cours": "bg-amber-100 text-amber-700",
  "Devis envoyé": "bg-purple-100 text-purple-700", "Signé": "bg-green-100 text-green-700", "Perdu": "bg-red-100 text-red-700",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/leads", { params: filter !== "all" ? { status: filter } : {} }).then((r) => setLeads(r.data));
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const update = async (id, payload) => {
    const { data } = await api.put(`/leads/${id}`, payload);
    setSel(data); load();
    toast.success("Lead mis à jour");
  };

  const addNote = async () => { if (!note.trim()) return; await update(sel.id, { note }); setNote(""); };

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1">Gare de triage des leads</h1>
      <p className="text-stone mb-6">Qualifiez et suivez vos demandes entrantes</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`lead-filter-${s}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === s ? "bg-brand text-cream" : "bg-white border border-border text-forest hover:border-brand"}`}>
            {s === "all" ? "Tous" : s}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
        <div className="space-y-3">
          {leads.map((l) => (
            <button key={l.id} onClick={() => setSel(l)} data-testid={`lead-${l.id}`}
              className={`w-full text-left rounded-xl bg-white border p-4 transition-all ${sel?.id === l.id ? "border-brand ring-2 ring-brand/20" : "border-border hover:border-brand"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-forest">{l.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${statusColor[l.status] || "bg-stone/10"}`}>{l.status}</span>
              </div>
              <p className="text-xs text-stone flex items-center gap-3">
                <span className="flex items-center gap-1"><Phone size={12} />{l.phone || "—"}</span>
                <span>{l.segment}</span>
                {l.source === "chat" && <span className="flex items-center gap-1 text-terracotta"><MessageSquare size={12} />chat</span>}
                {l.tag && <span className="rounded-full bg-terracotta/15 text-terracotta px-2 py-0.5 text-[10px] font-semibold" data-testid="lead-tag">{l.tag}</span>}
              </p>
              {l.service && <p className="text-xs text-stone mt-1">{l.service}</p>}
            </button>
          ))}
          {leads.length === 0 && <p className="text-stone py-10 text-center">Aucun lead.</p>}
        </div>

        {sel && (
          <div className="rounded-xl bg-white border border-border p-6 h-fit lg:sticky lg:top-6" data-testid="lead-detail">
            <h2 className="font-display text-2xl text-forest">{sel.name}</h2>
            <div className="mt-2 space-y-1 text-sm text-stone">
              {sel.phone && <p className="flex items-center gap-2"><Phone size={14} /> {sel.phone}</p>}
              {sel.email && <p className="flex items-center gap-2"><Mail size={14} /> {sel.email}</p>}
              {sel.city && <p className="flex items-center gap-2"><MapPin size={14} /> {sel.city}</p>}
            </div>
            {sel.message && <p className="mt-3 text-sm bg-sage/50 rounded-lg p-3 text-forest">{sel.message}</p>}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div><label className="text-xs text-stone">Statut</label>
                <select value={sel.status} onChange={(e) => update(sel.id, { status: e.target.value })} data-testid="lead-status" className="w-full rounded-lg border border-border px-3 py-2 text-sm">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              </div>
              <div><label className="text-xs text-stone">Qualification</label>
                <select value={sel.quality} onChange={(e) => update(sel.id, { quality: e.target.value })} data-testid="lead-quality" className="w-full rounded-lg border border-border px-3 py-2 text-sm">{QUALITIES.map((q) => <option key={q}>{q}</option>)}</select>
              </div>
            </div>
            <div className="mt-3"><label className="text-xs text-stone">Assigné à</label>
              <select value={sel.assigned_to || ""} onChange={(e) => update(sel.id, { assigned_to: e.target.value || null })} data-testid="lead-assign" className="w-full rounded-lg border border-border px-3 py-2 text-sm">
                <option value="">Non assigné</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold text-forest mb-2 flex items-center gap-1"><Star size={13} /> Historique de traitement</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(sel.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="text-xs border-l-2 border-brand pl-3 py-0.5">
                    <p className="text-forest font-medium">{h.action}</p>
                    {h.note && <p className="text-stone">{h.note}</p>}
                    <p className="text-stone/50">{new Date(h.date).toLocaleString("fr-FR")}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Ajouter une note…" data-testid="lead-note-input" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
                <button onClick={addNote} data-testid="lead-note-add" className="rounded-lg bg-brand text-cream px-3 hover:bg-forest"><Send size={16} /></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
